import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { DocumentTagEnum, FundAmountEnum } from '../../../../common/interfaces'
import { customSwal } from '../../../../config/swal'
import { BackofficePermissionsEnum as Permissions } from '../../../../common/enums/backOfficePermissionsEnum'
import usePermissions from '../../../../common/hooks/usePermissions'
import {
  FundDraftService,
  IContentCommissionItem,
  IContentFeatureItem,
  IContentTab,
} from '../../../../services/FundDraftService'
import { TabSkeleton } from '../../../../common/components/TabSkeleton'
import { useFundSeries } from '../hooks/useFundSeries'
import { UploadFilesV2 } from './UploadFilesV2'

const FIXED_COMMISSION_LABELS = ['Comisión unificada', 'Comisión de rescate anticipado', 'Impuesto a la renta']

interface IProps {
  fundId: string
  onDirtyChange?: (dirty: boolean) => void
  saveRef?: React.MutableRefObject<(() => Promise<void>) | null>
}

export const ContentTab: React.FC<IProps> = ({ fundId, onDirtyChange, saveRef }) => {
  const { hasPermission } = usePermissions()
  const { data: seriesData, isLoading, refetchSeriesOnly } = useFundSeries(fundId)
  const queryClient = useQueryClient()

  const [featureRows, setFeatureRows] = useState<IContentFeatureItem[]>([{ label: '', values: {} }])
  const [commissionRows, setCommissionRows] = useState<IContentCommissionItem[]>([
    { name: '', values: {} },
  ])
  const [fixedCommissionRows, setFixedCommissionRows] = useState<IContentCommissionItem[]>(
    FIXED_COMMISSION_LABELS.map((name) => ({ name, values: {} }))
  )
  const [amountInversionRows, setAmountInversionRows] = useState<IContentFeatureItem[]>(
    Object.values(FundAmountEnum).map((label) => ({ label, values: {} }))
  )
  const [contentAction, setContentAction] = useState<'saving' | 'publishing' | null>(null)
  const [dirtyDocuments, setDirtyDocuments] = useState<
    Partial<Record<DocumentTagEnum, File | null>>
  >({})
  const [dirtyRenames, setDirtyRenames] = useState<
    Partial<Record<DocumentTagEnum, { name: string; id: number }>>
  >({})

  const [channelAvailability, setChannelAvailability] = useState<Record<string, boolean>>({})
  const [featuresVisibility, setFeaturesVisibility] = useState(true)
  const [commissionsTitle, setCommissionsTitle] = useState('')
  const [commissionsVisibility, setCommissionsVisibility] = useState(true)
  const [disclaimerTitle, setDisclaimerTitle] = useState('')
  const [disclaimer, setDisclaimer] = useState('')
  const [disclaimerVisibility, setDisclaimerVisibility] = useState(true)
  const [legalVisibility, setLegalVisibility] = useState(true)
  const [legalTitle, setLegalTitle] = useState('')
  const [legalSubtitle, setLegalSubtitle] = useState('')
  const [legalDescription, setLegalDescription] = useState('')
  const [document, setDocument] = useState('')

  const [tabDirty, setTabDirty] = useState(false)
  const isDirty =
    tabDirty ||
    Object.keys(dirtyDocuments).length > 0 ||
    Object.keys(dirtyRenames).length > 0

  const initialized = useRef(false)
  const skipDirtyCount = useRef(1)

  useEffect(() => {
    if (initialized.current || !seriesData) return
    const firstCode = seriesData.series[0]?.serie_codigo
    if (!firstCode) return
    initialized.current = true

    const availability: Record<string, boolean> = {}
    for (const serie of seriesData.series) {
      const sc = seriesData.tabs.content[serie.serie_codigo]
      availability[serie.serie_codigo] = sc?.channelAvailability ?? false
    }
    setChannelAvailability(availability)

    const firstContent = seriesData.tabs.content[firstCode]
    if (firstContent) {
      setDocument(firstContent.document ?? '')
      setFeaturesVisibility(firstContent.features?.visibility ?? true)
      setCommissionsTitle(firstContent.commissions?.title ?? '')
      setCommissionsVisibility(firstContent.commissions?.visibility ?? true)
      setDisclaimerTitle(firstContent.disclaimer?.title ?? '')
      setDisclaimer(firstContent.disclaimer?.description ?? '')
      setDisclaimerVisibility(firstContent.disclaimer?.visibility ?? true)
      setLegalVisibility(firstContent.legal?.visibility ?? true)
      setLegalTitle(firstContent.legal?.title ?? '')
      setLegalSubtitle(firstContent.legal?.subtitle ?? '')
      setLegalDescription(firstContent.legal?.description ?? '')
    }

    // Merge feature and commission rows across all series (API returns value:string per serie)
    const featuresByLabel: Record<string, Record<string, string>> = {}
    const commissionsByName: Record<string, Record<string, string>> = {}
    for (const serie of seriesData.series) {
      const sc = seriesData.tabs.content[serie.serie_codigo]
      if (!sc) continue
      sc.features?.data?.forEach((item: any) => {
        if (!featuresByLabel[item.label]) featuresByLabel[item.label] = {}
        featuresByLabel[item.label][serie.serie_codigo] = item.value ?? ''
      })
      sc.commissions?.data?.forEach((item: any) => {
        if (!commissionsByName[item.name]) commissionsByName[item.name] = {}
        commissionsByName[item.name][serie.serie_codigo] = item.value ?? ''
      })
    }

    const mergedFeatureRows = Object.entries(featuresByLabel).map(([label, values]) => ({
      label,
      values,
    }))
    const mergedCommissionRows = Object.entries(commissionsByName).map(([name, values]) => ({
      name,
      values,
    }))

    // Split features: labels fijos de FundAmountEnum → amountInversionRows, resto → featureRows
    const amountLabels = new Set<string>(Object.values(FundAmountEnum))
    const amountRowsFromApi = mergedFeatureRows.filter((r) => amountLabels.has(r.label))
    const dynamicFeatureRows = mergedFeatureRows.filter((r) => !amountLabels.has(r.label))
    // Siempre mostrar todos los labels del enum; rellenar con valores del API si existen
    const mergedAmountRows = Object.values(FundAmountEnum).map((label) => ({
      label,
      values: amountRowsFromApi.find((r) => r.label === label)?.values ?? {},
    }))
    setAmountInversionRows(mergedAmountRows)
    if (dynamicFeatureRows.length) setFeatureRows(dynamicFeatureRows)

    // Split commissions: nombres fijos → fixedCommissionRows, resto → commissionRows
    const fixedCommissionSet = new Set<string>(FIXED_COMMISSION_LABELS)
    const fixedFromApi = mergedCommissionRows.filter((r) => fixedCommissionSet.has(r.name))
    const dynamicCommissionRows = mergedCommissionRows.filter((r) => !fixedCommissionSet.has(r.name))
    const mergedFixedCommissionRows = FIXED_COMMISSION_LABELS.map((name) => ({
      name,
      values: fixedFromApi.find((r) => r.name === name)?.values ?? {},
    }))
    setFixedCommissionRows(mergedFixedCommissionRows)
    if (dynamicCommissionRows.length) setCommissionRows(dynamicCommissionRows)
    skipDirtyCount.current += 1
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesData])

  const addCaracther = (e: React.MouseEvent) => {
    e.preventDefault()
    const emptyValues = (seriesData?.series ?? []).reduce<Record<string, string>>((acc, s) => {
      acc[s.serie_codigo] = ''
      return acc
    }, {})
    setFeatureRows([...featureRows, { label: '', values: emptyValues }])
  }

  const addComission = (e: React.MouseEvent) => {
    e.preventDefault()
    const emptyValues = (seriesData?.series ?? []).reduce<Record<string, string>>((acc, s) => {
      acc[s.serie_codigo] = ''
      return acc
    }, {})
    setCommissionRows([...commissionRows, { name: '', values: emptyValues }])
  }

  const removeItemCaracters = (e: React.MouseEvent, index: number) => {
    e.preventDefault()
    setFeatureRows(featureRows.filter((_, i) => i !== index))
  }

  const removeItemComission = (e: React.MouseEvent, index: number) => {
    e.preventDefault()
    setCommissionRows(commissionRows.filter((_, i) => i !== index))
  }

  const buildPayload = (): IContentTab => ({
    channelAvailability,
    document: document || undefined,
    features: {
      visibility: featuresVisibility,
      data: [...amountInversionRows, ...featureRows],
    },
    commissions: {
      visibility: commissionsVisibility,
      title: commissionsTitle,
      data: [...fixedCommissionRows, ...commissionRows],
    },
    disclaimer: {
      visibility: disclaimerVisibility,
      title: disclaimerTitle,
      description: disclaimer,
    },
    legal: {
      visibility: legalVisibility,
      title: legalTitle,
      subtitle: legalSubtitle,
      description: legalDescription,
    },
  })

  const uploadDirtyDocuments = async () => {
    const entries = Object.entries(dirtyDocuments).filter(
      (entry): entry is [string, File] => entry[1] !== null && entry[1] !== undefined
    )
    if (entries.length > 0) {
      await Promise.all(
        entries.map(([tag, file]) => FundDraftService().uploadDocumentDraft(fundId, file, tag))
      )
      setDirtyDocuments({})
    }
  }

  const uploadDirtyRenames = async () => {
    const entries = Object.entries(dirtyRenames) as [
      DocumentTagEnum,
      { name: string; id: number }
    ][]
    if (entries.length > 0) {
      await FundDraftService().renameDocumentDraft(
        fundId,
        entries.map(([, { name, id }]) => ({ id, constancy_filename: name }))
      )
      setDirtyRenames({})
    }
  }

  const saveAll = () =>
    Promise.all([
      uploadDirtyDocuments(),
      uploadDirtyRenames(),
      FundDraftService().updateContentTab(fundId, buildPayload()),
    ])

  const handleSave = async () => {
    setContentAction('saving')
    try {
      await saveAll()
      setTabDirty(false)
      refetchSeriesOnly()
      queryClient.invalidateQueries(['document-draft', fundId])
      customSwal.fire({ title: 'Contenido guardado correctamente', icon: 'success' })
    } catch {
      customSwal.fire({ title: 'Error al guardar el contenido', icon: 'error' })
    }
    setContentAction(null)
  }

  const handlePublish = async () => {
    setContentAction('publishing')
    try {
      await saveAll()
      await Promise.all([
        FundDraftService().publishContentTab(fundId),
        FundDraftService()
          .publishDocumentDraft(fundId)
          .catch((err) => {
            if (!err || !err.response || err.response.status !== 404) throw err
          }),
      ])
      setTabDirty(false)
      refetchSeriesOnly()
      queryClient.invalidateQueries(['document-draft', fundId])
      customSwal.fire({ title: 'Contenido publicado correctamente', icon: 'success' })
    } catch {
      customSwal.fire({ title: 'Error al publicar el contenido', icon: 'error' })
    }
    setContentAction(null)
  }

  // Mark dirty when any user-editable state changes (skip load-triggered fires)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (skipDirtyCount.current > 0) { skipDirtyCount.current -= 1; return }
    setTabDirty(true)
  }, [
    featureRows,
    commissionRows,
    fixedCommissionRows,
    amountInversionRows,
    featuresVisibility,
    commissionsTitle,
    commissionsVisibility,
    disclaimerTitle,
    disclaimer,
    disclaimerVisibility,
    legalVisibility,
    legalTitle,
    legalSubtitle,
    legalDescription,
    channelAvailability,
    document,
  ])

  // Report dirty state to ConfigFunds
  useEffect(() => {
    onDirtyChange?.(isDirty)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty])

  // Keep saveRef current
  useEffect(() => {
    if (saveRef) saveRef.current = handleSave
  })

  if (isLoading && !seriesData) return <TabSkeleton />

  return (
    <div>
      <div className='card mt-10 p-5'>
        <div className='d-flex align-items-center gap-3 mt-2 mb-1'>
          <span className='text-gray-800 fs-2 fw-bold'>Contenido</span>
          {seriesData && (
            <span
              style={{
                background: '#d4edda',
                color: '#155724',
                fontSize: '12px',
                padding: '2px 10px',
                borderRadius: 20,
                fontWeight: 600,
              }}
            >
              Edición activo
            </span>
          )}
        </div>
      </div>

      {/* Disponible en canales */}
      {seriesData && seriesData.series.length > 0 && (
        <div className='card mt-10 p-10'>
          <span className='card-title text-start text-gray-800 fs-2 d-block mb-5'>
            Disponible en canales
          </span>
          <div className='d-flex flex-wrap gap-6'>
            {seriesData.series.map((serie) => (
              <div key={serie.serie_codigo} className='d-flex flex-column gap-2'>
                <span className='fw-bolder fs-6 text-gray-700'>
                  {serie.serie_name}
                </span>
                <div
                  style={{
                    display: 'inline-flex',
                    border: '1px solid #d1d5db',
                    borderRadius: 8,
                    overflow: 'hidden',
                  }}
                >
                  <button
                    type='button'
                    onClick={() =>
                      setChannelAvailability((prev) => ({
                        ...prev,
                        [serie.serie_codigo]: true,
                      }))
                    }
                    disabled={!hasPermission(Permissions.FUND_TABS.CONTENT.CHANNEL_EDIT)}
                    style={{
                      padding: '6px 20px',
                      border: 'none',
                      background: channelAvailability[serie.serie_codigo] === true ? 'var(--color-primary-blue)' : '#fff',
                      color: channelAvailability[serie.serie_codigo] === true ? '#fff' : '#374151',
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: hasPermission(Permissions.FUND_TABS.CONTENT.CHANNEL_EDIT) ? 'pointer' : 'not-allowed',
                    }}
                  >
                    SÍ
                  </button>
                  <button
                    type='button'
                    onClick={() =>
                      setChannelAvailability((prev) => ({
                        ...prev,
                        [serie.serie_codigo]: false,
                      }))
                    }
                    disabled={!hasPermission(Permissions.FUND_TABS.CONTENT.CHANNEL_EDIT)}
                    style={{
                      padding: '6px 20px',
                      border: 'none',
                      borderLeft: '1px solid #d1d5db',
                      background: channelAvailability[serie.serie_codigo] === false ? 'var(--color-primary-blue)' : '#fff',
                      color: channelAvailability[serie.serie_codigo] === false ? '#fff' : '#374151',
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: hasPermission(Permissions.FUND_TABS.CONTENT.CHANNEL_EDIT) ? 'pointer' : 'not-allowed',
                    }}
                  >
                    NO
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Características */}
      <div className='card mt-10 p-10 pb-5'>
        <div className='d-flex align-items-center justify-content-between mb-3'>
          <span className='card-title text-start text-gray-800 fs-2'>Características</span>
          <button
            style={{ color: 'white', marginRight: '2px', background: 'rgb(0, 123, 195)' }}
            className='btn mr-3'
            onClick={addCaracther}
            disabled={!featuresVisibility || !hasPermission(Permissions.FUND_TABS.CONTENT.FEATURES_EDIT)}
          >
            <i style={{ color: 'white' }} className='bi bi-plus mr-2 fs-1'></i>
            <span>Agregar campo</span>
          </button>
        </div>
        <div className='d-flex align-items-center gap-3 mb-3'>
          <label className='fw-bolder fs-6 text-gray-700 mb-0'>Mostrar información</label>
          <div className='form-check form-switch m-0'>
            <input
              className='form-check-input'
              type='checkbox'
              role='switch'
              checked={featuresVisibility}
              onChange={(e) => setFeaturesVisibility(e.target.checked)}
              disabled={!hasPermission(Permissions.FUND_TABS.CONTENT.FEATURES_EDIT)}
            />
          </div>
        </div>
        <table className='table table-row-dashed table-row-gray-300 gy-7'>
          <thead>
            <tr className='fw-bolder fs-6 text-gray-700'>
              <th></th>
              <th>NOMBRE</th>
              {(seriesData?.series ?? []).map((s) => (
                <th key={s.serie_codigo}>{s.serie_name.toUpperCase()}</th>
              ))}
              <th className='text-center'>ACCIONES</th>
            </tr>
          </thead>
          <tbody>
            {amountInversionRows.map((row, index) => (
              <tr key={`amount-${index}`}>
                <td>
                  <div className='mt-5'>{index + 1}</div>
                </td>
                <td>
                  <input
                    className='form-control mb-2 mb-md-0'
                    type='text'
                    value={row.label}
                    disabled
                  />
                </td>
                {(seriesData?.series ?? []).map((s) => (
                  <td key={s.serie_codigo}>
                    <input
                      type='text'
                      className='form-control mb-2 mb-md-0'
                      placeholder={s.serie_name}
                      value={row.values?.[s.serie_codigo] ?? ''}
                      onChange={(e) => {
                        const updated = [...amountInversionRows]
                        updated[index] = {
                          ...updated[index],
                          values: { ...updated[index].values, [s.serie_codigo]: e.target.value },
                        }
                        setAmountInversionRows(updated)
                      }}
                      disabled={!featuresVisibility || !hasPermission(Permissions.FUND_TABS.CONTENT.FEATURES_EDIT)}
                    />
                  </td>
                ))}
                <td />
              </tr>
            ))}
            {featureRows.map((row, index) => (
              <tr key={index}>
                <td>
                  <div className='mt-5'>{amountInversionRows.length + index + 1}</div>
                </td>
                <td>
                  <input
                    className='form-control mb-2 mb-md-0'
                    placeholder='Nombre'
                    type='text'
                    value={row.label}
                    onChange={(e) => {
                      const updated = [...featureRows]
                      updated[index] = { ...updated[index], label: e.target.value }
                      setFeatureRows(updated)
                    }}
                    disabled={!featuresVisibility || !hasPermission(Permissions.FUND_TABS.CONTENT.FEATURES_EDIT)}
                  />
                </td>
                {(seriesData?.series ?? []).map((s) => (
                  <td key={s.serie_codigo}>
                    <input
                      type='text'
                      className='form-control mb-2 mb-md-0'
                      placeholder={s.serie_name}
                      value={row.values?.[s.serie_codigo] ?? ''}
                      onChange={(e) => {
                        const updated = [...featureRows]
                        updated[index] = {
                          ...updated[index],
                          values: { ...updated[index].values, [s.serie_codigo]: e.target.value },
                        }
                        setFeatureRows(updated)
                      }}
                      disabled={!featuresVisibility || !hasPermission(Permissions.FUND_TABS.CONTENT.FEATURES_EDIT)}
                    />
                  </td>
                ))}
                <td className='text-center'>
                  <button
                    disabled={!featuresVisibility || !hasPermission(Permissions.FUND_TABS.CONTENT.FEATURES_EDIT)}
                    style={{ background: 'transparent', color: 'white', border: '1px solid #ccc' }}
                    className='btn btn-icon ml-3'
                    onClick={(e) => removeItemCaracters(e, index)}
                  >
                    <i className='bi bi-trash text-muted fs-3'></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className='text-end'>
          {featureRows.length === 0 && (
            <button
              disabled={!featuresVisibility || !hasPermission(Permissions.FUND_TABS.CONTENT.FEATURES_EDIT)}
              style={{ background: 'var(--color-primary-blue)', color: 'white', marginRight: '2px' }}
              className='btn mr-3'
              onClick={addCaracther}
            >
              <i style={{ color: 'white' }} className='bi bi-plus fs-1'></i> Agregar fila
            </button>
          )}
        </div>
      </div>

      {/* Comisiones */}
      <div className='card mt-10 p-10 pb-5'>
        <div className='d-flex align-items-center justify-content-between mb-3'>
          <span className='card-title text-start text-gray-800 fs-2'>Comisiones</span>
          <button
            style={{ color: 'white', marginRight: '2px', background: 'rgb(0, 123, 195)' }}
            className='btn mr-3'
            onClick={addComission}
            disabled={!commissionsVisibility || !hasPermission(Permissions.FUND_TABS.CONTENT.COMMISSIONS_EDIT)}
          >
            <i style={{ color: 'white' }} className='bi bi-plus mr-2 fs-1'></i>
            <span>Agregar campo</span>
          </button>
        </div>
        <div className='d-flex align-items-center gap-3 mb-3'>
          <label className='fw-bolder fs-6 text-gray-700 mb-0'>Mostrar información</label>
          <div className='form-check form-switch m-0'>
            <input
              className='form-check-input'
              type='checkbox'
              role='switch'
              checked={commissionsVisibility}
              onChange={(e) => setCommissionsVisibility(e.target.checked)}
              disabled={!hasPermission(Permissions.FUND_TABS.CONTENT.COMMISSIONS_EDIT)}
            />
          </div>
        </div>
        <div className='mt-5 mb-3'>
          <label htmlFor='Diabled' className='fw-bolder fs-7 text-gray-700'>
            TÍTULO
          </label>
          <input
            className='form-control mb-2 mb-md-0'
            placeholder='Título'
            type='text'
            value={commissionsTitle}
            onChange={(e) => setCommissionsTitle(e.target.value)}
            disabled={!hasPermission(Permissions.FUND_TABS.CONTENT.COMMISSIONS_EDIT)}
          />
        </div>
        <table className='table table-row-dashed table-row-gray-300 gy-7'>
          <thead>
            <tr className='fw-bolder fs-6 text-gray-700'>
              <th></th>
              <th>NOMBRE</th>
              {(seriesData?.series ?? []).map((s) => (
                <th key={s.serie_codigo}>{s.serie_name.toUpperCase()}</th>
              ))}
              <th className='text-center'>ACCIONES</th>
            </tr>
          </thead>
          <tbody>
            {fixedCommissionRows.map((row, index) => (
              <tr key={`fixed-${index}`}>
                <td>
                  <div className='mt-5'>{index + 1}</div>
                </td>
                <td>
                  <input
                    className='form-control mb-2 mb-md-0'
                    type='text'
                    value={row.name}
                    disabled
                  />
                </td>
                {(seriesData?.series ?? []).map((s) => (
                  <td key={s.serie_codigo}>
                    <input
                      type='text'
                      className='form-control mb-2 mb-md-0'
                      placeholder={s.serie_name}
                      value={row.values?.[s.serie_codigo] ?? ''}
                      onChange={(e) => {
                        const updated = [...fixedCommissionRows]
                        updated[index] = {
                          ...updated[index],
                          values: { ...updated[index].values, [s.serie_codigo]: e.target.value },
                        }
                        setFixedCommissionRows(updated)
                      }}
                      disabled={!commissionsVisibility || !hasPermission(Permissions.FUND_TABS.CONTENT.COMMISSIONS_EDIT)}
                    />
                  </td>
                ))}
                <td />
              </tr>
            ))}
            {commissionRows.map((row, index) => (
              <tr key={index}>
                <td>
                  <div className='mt-5'>{fixedCommissionRows.length + index + 1}</div>
                </td>
                <td>
                  <input
                    className='form-control mb-2 mb-md-0'
                    placeholder='Nombre'
                    type='text'
                    value={row.name}
                    onChange={(e) => {
                      const updated = [...commissionRows]
                      updated[index] = { ...updated[index], name: e.target.value }
                      setCommissionRows(updated)
                    }}
                    disabled={!commissionsVisibility || !hasPermission(Permissions.FUND_TABS.CONTENT.COMMISSIONS_EDIT)}
                  />
                </td>
                {(seriesData?.series ?? []).map((s) => (
                  <td key={s.serie_codigo}>
                    <input
                      type='text'
                      className='form-control mb-2 mb-md-0'
                      placeholder={s.serie_name}
                      value={row.values?.[s.serie_codigo] ?? ''}
                      onChange={(e) => {
                        const updated = [...commissionRows]
                        updated[index] = {
                          ...updated[index],
                          values: { ...updated[index].values, [s.serie_codigo]: e.target.value },
                        }
                        setCommissionRows(updated)
                      }}
                      disabled={!commissionsVisibility || !hasPermission(Permissions.FUND_TABS.CONTENT.COMMISSIONS_EDIT)}
                    />
                  </td>
                ))}
                <td className='text-center'>
                  <button
                    disabled={!commissionsVisibility || !hasPermission(Permissions.FUND_TABS.CONTENT.COMMISSIONS_EDIT)}
                    style={{ background: 'transparent', color: 'white', border: '1px solid #ccc' }}
                    className='btn btn-icon ml-3'
                    onClick={(e) => removeItemComission(e, index)}
                  >
                    <i className='bi bi-trash text-muted fs-3'></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className='text-end'>
          {commissionRows.length === 0 && (
            <button
              disabled={!commissionsVisibility || !hasPermission(Permissions.FUND_TABS.CONTENT.COMMISSIONS_EDIT)}
              style={{ background: 'var(--color-primary-blue)', color: 'white', marginRight: '2px' }}
              className='btn mr-3'
              onClick={addComission}
            >
              <i style={{ color: 'white' }} className='bi bi-plus fs-1'></i> Agregar fila
            </button>
          )}
        </div>
      </div>

      {/* Documento */}
      <div className='card mt-10 p-10'>
        <span className='card-title text-start text-gray-800 fs-2 d-block mb-3'>Documento</span>
        <div className='mb-3'>
          <label className='fw-bolder fs-7 text-gray-700'>URL</label>
          <input
            className='form-control mb-2 mb-md-0'
            placeholder='https://'
            type='url'
            value={document}
            onChange={(e) => setDocument(e.target.value)}
          />
        </div>
      </div>

      <UploadFilesV2
        codFund={fundId}
        dirtyFiles={dirtyDocuments}
        onDirtyChange={(tag, file) => setDirtyDocuments((prev) => ({ ...prev, [tag]: file }))}
        dirtyRenames={
          Object.fromEntries(
            Object.entries(dirtyRenames).map(([tag, v]) => [tag, v.name])
          ) as Partial<Record<DocumentTagEnum, string>>
        }
        onRenameChange={(tag, name, id) =>
          setDirtyRenames((prev) => ({ ...prev, [tag]: { name, id } }))
        }
        onRenameClear={(tag) =>
          setDirtyRenames((prev) => {
            const next = { ...prev }
            delete next[tag]
            return next
          })
        }
        disableShow={!hasPermission(Permissions.FUND_TABS.CONTENT.DOCUMENT_SHOW)}
        disableUpload={!hasPermission(Permissions.FUND_TABS.CONTENT.DOCUMENT_UPLOAD)}
      />

      {/* Disclaimer */}
      <div className='card mt-10 p-10'>
        <span className='card-title text-start text-gray-800 fs-2 d-block mb-3'>Disclaimer</span>
        <div className='d-flex align-items-center gap-3 mb-3'>
          <label className='fw-bolder fs-6 text-gray-700 mb-0'>Mostrar información</label>
          <div className='form-check form-switch m-0'>
            <input
              className='form-check-input'
              type='checkbox'
              role='switch'
              checked={disclaimerVisibility}
              onChange={(e) => setDisclaimerVisibility(e.target.checked)}
              disabled={!hasPermission(Permissions.FUND_TABS.CONTENT.DISCLAIMER_EDIT)}
            />
          </div>
        </div>
        <div className='mt-5 mb-3'>
          <label htmlFor='Diabled' className='fw-bolder fs-7 text-gray-700'>
            TÍTULO
          </label>
          <input
            className='form-control mb-2 mb-md-0'
            placeholder='Título'
            type='text'
            value={disclaimerTitle}
            onChange={(e) => setDisclaimerTitle(e.target.value)}
            disabled={!hasPermission(Permissions.FUND_TABS.CONTENT.DISCLAIMER_EDIT)}
          />
        </div>
        <textarea
          className='form-control'
          placeholder='Ingrese el disclaimer'
          style={{ height: '100px', resize: 'none' }}
          value={disclaimer}
          onChange={(e) => setDisclaimer(e.target.value)}
          disabled={!hasPermission(Permissions.FUND_TABS.CONTENT.DISCLAIMER_EDIT)}
        />
      </div>

      {/* Legales */}
      <div className='card mt-10 p-10'>
        <span className='card-title text-start text-gray-800 fs-2 d-block mb-3'>Legales 
<span className='text-gray-600 fs-4'> (Valor cuota y rentabilidad)</span>

        </span>
        <div className='d-flex align-items-center gap-3 mb-5'>
          <label className='fw-bolder fs-6 text-gray-700 mb-0'>Mostrar información</label>
          <div className='form-check form-switch m-0'>
            <input
              className='form-check-input'
              type='checkbox'
              role='switch'
              checked={legalVisibility}
              onChange={(e) => setLegalVisibility(e.target.checked)}
              disabled={!hasPermission(Permissions.FUND_TABS.CONTENT.LEGAL_EDIT)}
            />
          </div>
        </div>
        <div className='mb-3'>
          <label className='fw-bolder fs-7 text-gray-700'>TÍTULO</label>
          <input
            className='form-control mb-2 mb-md-0'
            placeholder='Título'
            type='text'
            value={legalTitle}
            onChange={(e) => setLegalTitle(e.target.value)}
            disabled={!legalVisibility || !hasPermission(Permissions.FUND_TABS.CONTENT.LEGAL_EDIT)}
          />
        </div>
        <div className='mb-3'>
          <label className='fw-bolder fs-7 text-gray-700'>SUBTÍTULO</label>
          <input
            className='form-control mb-2 mb-md-0'
            placeholder='Subtítulo'
            type='text'
            value={legalSubtitle}
            onChange={(e) => setLegalSubtitle(e.target.value)}
            disabled={!legalVisibility || !hasPermission(Permissions.FUND_TABS.CONTENT.LEGAL_EDIT)}
          />
        </div>
        <div className='mb-3'>
          <label className='fw-bolder fs-7 text-gray-700'>DESCRIPCIÓN</label>
          <textarea
            className='form-control'
            placeholder='Descripción'
            style={{ height: '100px', resize: 'none' }}
            value={legalDescription}
            onChange={(e) => setLegalDescription(e.target.value)}
            disabled={!legalVisibility || !hasPermission(Permissions.FUND_TABS.CONTENT.LEGAL_EDIT)}
          />
        </div>
      </div>

      {/* Acciones */}
      {hasPermission([
        Permissions.FUND_TABS.CONTENT.SAVE,
        Permissions.FUND_TABS.CONTENT.PUBLISH,
      ]) && (
        <div className='card mt-5 p-5'>
          <div className='d-flex justify-content-end gap-3'>
            {hasPermission(Permissions.FUND_TABS.CONTENT.SAVE) && (
              <button
                type='button'
                className='btn btn-custom-action'
                onClick={handleSave}
                disabled={contentAction !== null}
              >
                {contentAction === 'saving' ? (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    Guardando...
                    <span className='spinner-border spinner-border-sm align-middle ms-2' />
                  </span>
                ) : (
                  'Guardar cambios'
                )}
              </button>
            )}
            {hasPermission(Permissions.FUND_TABS.CONTENT.PUBLISH) && (
              <button
                type='button'
                className='btn btn-custom-action'
                onClick={handlePublish}
                disabled={contentAction !== null}
              >
                {contentAction === 'publishing' ? (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    Publicando...
                    <span className='spinner-border spinner-border-sm align-middle ms-2' />
                  </span>
                ) : (
                  'Publicar'
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
